import React, { useState, useEffect } from 'react';
import { FaUsers, FaPlus, FaEdit, FaTrash, FaExclamationTriangle, FaShieldAlt, FaEye, FaEyeSlash } from 'react-icons/fa';
import { getCustomerUsers, deleteCustomerUser, isDefaultUser, getUserDisplayName, getUserInitials, formatLastLogin } from '../../services/CustomerUserService';
import { canManageUsers } from '../../utils/permissions';
import UserModal from './UserModal';

const SaasUserManagementSection = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await getCustomerUsers();
      setUsers(data);
      setError('');
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Get current user from localStorage
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const userData = JSON.parse(userStr);
        setCurrentUser(userData);
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }
    
    fetchUsers();
  }, []);

  const handleCreateUser = () => {
    setEditingUser(null);
    setModalOpen(true);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setModalOpen(true);
  };

  const handleDeleteUser = async (user) => {
    try {
      await deleteCustomerUser(user.id);
      await fetchUsers();
      setDeleteConfirm(null);
    } catch (err) {
      setError('Failed to delete user');
      console.error('Error deleting user:', err);
    }
  };

  const handleModalSuccess = () => {
    fetchUsers();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
        {canManageUsers(currentUser) && (
          <button
            onClick={handleCreateUser}
            className="flex items-center px-4 py-2 bg-[#DB4F40] text-white rounded-md hover:bg-[#C64537] transition-all duration-200"
          >
            <FaPlus className="w-4 h-4 mr-2" />
            Add User
          </button>
        )}
      </div>

      {/* Info about system users */}
      <div className="mb-6 p-4 bg-[#FFEEED] border border-[#FFD6D4] rounded-md">
        <div className="flex items-start">
          <FaShieldAlt className="w-5 h-5 text-[#DB4F40] mr-2 mt-0.5" />
          <div>
            <h3 className="text-sm font-medium text-[#DB4F40] mb-1">User Management</h3>
            <p className="text-sm text-[#1B222A]/80">
              You can see and manage all users belonging to your organization. 
              System users cannot be edited or deleted, but custom users can be fully managed.
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <div className="flex items-center">
            <FaExclamationTriangle className="w-5 h-5 text-red-500 mr-2" />
            <span className="text-red-700 font-medium">Error Loading Users</span>
          </div>
          <p className="text-red-700 mt-1">{error}</p>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Auth Provider
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Login
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((user) => {
                const isDefault = isDefaultUser(user);
                const displayName = getUserDisplayName(user);
                const initials = getUserInitials(user);
                
                return (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gradient-to-r from-[#DB4F40] to-[#C64537] rounded-full flex items-center justify-center mr-3">
                          <span className="text-white font-medium">
                            {initials}
                          </span>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {displayName}
                            {isDefault && (
                              <span className="ml-2 px-2 py-1 text-xs bg-[#FFD6D4] text-[#DB4F40] rounded-full">
                                System
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {user.role_name || 'No Role'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div className="flex items-center">
                        <span className="mr-2">🔐</span>
                        <span className="capitalize">Local</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        user.status === 'active' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {user.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {formatLastLogin(user.last_login_at)}
                    </td>
                    <td className="px-6 py-4">
                      {canManageUsers(currentUser) ? (
                        <div className="flex items-center space-x-2">
                          <button 
                            onClick={() => handleEditUser(user)}
                            className={`p-1 ${isDefault ? 'text-gray-300 cursor-not-allowed' : 'text-[#DB4F40] hover:text-[#C64537]'}`}
                            title={isDefault ? 'System users cannot be edited' : 'Edit user'}
                            disabled={isDefault}
                          >
                            <FaEdit className="w-4 h-4" />
                          </button>
                          <button 
                            onClick={() => setDeleteConfirm(user)}
                            className={`p-1 ${isDefault ? 'text-gray-300 cursor-not-allowed' : 'text-red-600 hover:text-red-900'}`}
                            title={isDefault ? 'System users cannot be deleted' : 'Delete user'}
                            disabled={isDefault}
                          >
                            <FaTrash className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">View only</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit Modal */}
      <UserModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        user={editingUser}
        onSuccess={handleModalSuccess}
      />

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center mb-4">
              <FaExclamationTriangle className="w-6 h-6 text-red-500 mr-2" />
              <h3 className="text-lg font-semibold text-gray-900">Delete User</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete the user "{getUserDisplayName(deleteConfirm)}"? This action cannot be undone.
            </p>
            {isDefaultUser(deleteConfirm) && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <p className="text-yellow-800 text-sm">
                  <strong>Warning:</strong> This is a system user. Deleting it may affect system functionality.
                </p>
              </div>
            )}
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteUser(deleteConfirm)}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SaasUserManagementSection;